import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "../../Components/Common/LoadingStates";
import config from "../../config";
import {
  getRewardsPayouts,
  recordRewardsPayout,
  cancelRewardsPayout,
  replayRewardsCredit,
} from "../../helpers/fakebackend_helper";
import { hasWallet, connect as walletConnect, ensureChain, sendToken, waitForReceipt } from "../../helpers/web3Payout";
import {
  TOKEN, card, btn, btnGhost, input, th, td, COLORS, busyBtn,
  shortAddr, fmtNum, fmtDate, Badge, Muted, ReadOnlyNote, Table, AppPlacement,
} from "./shared";
import { apiError } from "./rewardsUtil";

const R = config.rewards;
const chainOpts = { chainName: R.CHAIN_NAME, rpcUrl: R.CHAIN_RPC_URL, explorerUrl: R.CHAIN_EXPLORER_URL };

// Transfers that were sent but not yet recorded, keyed by claim id. Kept in
// localStorage so a reload does not lose the hash of a transfer that already
// moved funds (and so "Pay" stays locked for that claim).
const SENT_KEY = "rewards.sentUnrecorded";
function loadSent() {
  try { return JSON.parse(localStorage.getItem(SENT_KEY) || "{}") || {}; } catch { return {}; }
}
function saveSent(map) {
  try { localStorage.setItem(SENT_KEY, JSON.stringify(map)); } catch { /* storage unavailable */ }
}

function walletErrorText(err, fallback) {
  if (err && (err.code === 4001 || err.code === "ACTION_REJECTED")) return "You rejected the request in your wallet. Nothing was sent.";
  return apiError(err, fallback);
}

// ── Wallet connect bar ───────────────────────────────────────────────────────
export function WalletBar({ wallet, setWallet, flash }) {
  const onConnect = async () => {
    try {
      const w = await walletConnect();
      setWallet(w);
      flash("success", `Connected ${shortAddr(w.address)} (chain ${w.chainId})`);
    } catch (err) {
      flash("error", walletErrorText(err, "Wallet connection failed"));
    }
  };
  const wrongChain = wallet && Number(wallet.chainId) !== Number(R.CHAIN_ID);
  return (
    <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontWeight: 600 }}>Treasury wallet</div>
        <div style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 13 }}>
          {wallet ? `${shortAddr(wallet.address)} · chain ${wallet.chainId}` : "Not connected"}
          {" · payouts go out on "}{R.CHAIN_NAME} ({R.CHAIN_ID})
        </div>
        {wrongChain && (
          <div style={{ color: COLORS.warn, fontSize: 12, marginTop: 4 }}>
            Your wallet is on another network. Paying a claim asks it to switch to {R.CHAIN_NAME} first.
          </div>
        )}
      </div>
      <button style={btn} onClick={onConnect}>{wallet ? "Reconnect" : "Connect wallet"}</button>
    </div>
  );
}

// ── Payouts tab ──────────────────────────────────────────────────────────────
export function PayoutsTab({ wallet, setWallet, flash, canPay }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sent, setSentState] = useState(loadSent);
  const [failures, setFailures] = useState({}); // claim_id -> message

  const setSent = useCallback((updater) => {
    setSentState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveSent(next);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getRewardsPayouts();
      const list = res?.payouts || [];
      setRows(list);
      // A claim that left the queue was recorded (or cancelled) elsewhere.
      const open = new Set(list.map((r) => String(r.claim_id)));
      setSent((prev) => {
        const next = {};
        for (const [id, v] of Object.entries(prev)) if (open.has(id)) next[id] = v;
        return next;
      });
    } catch (err) {
      flash("error", apiError(err, "Failed to load payouts"));
      setRows([]);
    }
  }, [flash, setSent]);
  useEffect(() => { load(); }, [load]);

  const fail = (id, text) => {
    setFailures((f) => ({ ...f, [id]: text }));
    flash("error", text);
  };
  const clearFail = (id) => setFailures((f) => { const n = { ...f }; delete n[id]; return n; });

  const record = async (row, txHash) => {
    try {
      await recordRewardsPayout(row.claim_id, txHash);
      setSent((prev) => { const n = { ...prev }; delete n[row.claim_id]; return n; });
      clearFail(row.claim_id);
      flash("success", `Paid claim #${row.claim_id}`);
      await load();
    } catch (err) {
      fail(row.claim_id, `The transfer was sent but recording it failed (${apiError(err, "unknown error").replace(/\.$/, "")}). The tx hash is kept below: use Retry record once it has confirmed.`);
    }
  };

  const pay = async (row) => {
    if (!R.TOKEN_ADDRESS) {
      flash("error", "WANK token address is not configured (REACT_APP_WANK_TOKEN_ADDRESS).");
      return;
    }
    if (!hasWallet()) { flash("error", "No wallet detected. Install MetaMask."); return; }
    setBusyId(row.claim_id);
    clearFail(row.claim_id);
    let txHash = null;
    try {
      let w = wallet;
      if (!w) { w = await walletConnect(); setWallet(w); }
      await ensureChain(R.CHAIN_ID, chainOpts);
      setWallet({ ...w, chainId: Number(R.CHAIN_ID) });
      flash("info", "Confirm the transfer in your wallet.");
      txHash = await sendToken({
        from: w.address,
        tokenAddress: R.TOKEN_ADDRESS,
        to: row.to,
        amount: row.amount_byb,
        decimals: R.TOKEN_DECIMALS,
        chainId: R.CHAIN_ID,
      });
      // Funds have left the wallet: remember the hash before anything else can fail.
      setSent((prev) => ({ ...prev, [row.claim_id]: { txHash, at: new Date().toISOString() } }));
      flash("info", "Waiting for on-chain confirmation.");
      await waitForReceipt(txHash);
    } catch (err) {
      if (!txHash) {
        fail(row.claim_id, `Payout failed: ${walletErrorText(err, "the wallet did not send the transfer.")}`);
      } else if (err && err.reverted) {
        setSent((prev) => { const n = { ...prev }; delete n[row.claim_id]; return n; });
        fail(row.claim_id, "The transfer reverted on-chain, so nothing was paid. Check the treasury balance and try again.");
      } else {
        fail(row.claim_id, "The transfer was sent but has not confirmed yet. The tx hash is kept below: use Retry record once it confirms.");
      }
      setBusyId(null);
      return;
    }
    await record(row, txHash);
    setBusyId(null);
  };

  const retryRecord = async (row) => {
    const entry = sent[row.claim_id];
    if (!entry) return;
    setBusyId(row.claim_id);
    clearFail(row.claim_id);
    await record(row, entry.txHash);
    setBusyId(null);
  };

  const forget = (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Forget the saved transfer for claim #${row.claim_id}? Only do this if you are sure the transfer never went out, or the claim could be paid twice.`)) return;
    setSent((prev) => { const n = { ...prev }; delete n[row.claim_id]; return n; });
    clearFail(row.claim_id);
  };

  const cancel = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Cancel claim #${row.claim_id} and refund ${fmtNum(row.amount_byb)} points to the user?`)) return;
    setBusyId(row.claim_id);
    try {
      await cancelRewardsPayout(row.claim_id);
      flash("success", `Refunded claim #${row.claim_id}`);
      await load();
    } catch (err) {
      flash("error", `Cancel failed: ${apiError(err, "unknown error")}`);
    } finally {
      setBusyId(null);
    }
  };

  if (rows === null) return <div style={card}><Spinner /></div>;
  const awaiting = rows.filter((r) => r.awaiting_funding).length;

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h5 style={{ margin: 0 }}>Pending payouts ({rows.length})</h5>
          {awaiting > 0 && (
            <div style={{ color: COLORS.warn, fontSize: 12, marginTop: 4 }}>
              {awaiting} awaiting funding. Refill the rewards wallet to fund them, oldest first.
            </div>
          )}
        </div>
        <button style={btnGhost} onClick={load}>Refresh</button>
      </div>
      <AppPlacement warn>a recorded or cancelled payout is not shown to the user in the current app (no claim history screen until the Rewards update ships).</AppPlacement>
      {rows.length === 0 ? (
        <Muted>No payouts awaiting.</Muted>
      ) : (
        <Table head={["Claim", "To", `Amount (${TOKEN})`, "Requested", "Status", canPay ? "Action" : ""]}>
          {rows.map((r) => {
            const busy = busyId === r.claim_id;
            const entry = sent[r.claim_id];
            const failure = failures[r.claim_id];
            return (
              <React.Fragment key={r.claim_id}>
                <tr>
                  <td style={td}>#{r.claim_id}</td>
                  <td style={td} title={r.to}>{shortAddr(r.to)}</td>
                  <td style={td}>{fmtNum(r.amount_byb)}</td>
                  <td style={td}>{fmtDate(r.created_at)}</td>
                  <td style={td}>
                    {r.awaiting_funding ? <Badge>Awaiting funding</Badge> : entry ? <Badge tone="warn">Sent, not recorded</Badge> : <Badge tone="success">Ready to pay</Badge>}
                  </td>
                  <td style={td}>
                    {canPay && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {entry ? (
                          <>
                            <button style={busyBtn(busy)} disabled={busy} onClick={() => retryRecord(r)}>
                              {busy ? "Working…" : "Retry record"}
                            </button>
                            <button style={btnGhost} disabled={busy} onClick={() => forget(r)}>Forget transfer</button>
                          </>
                        ) : (
                          <button
                            style={busyBtn(busy || r.awaiting_funding)}
                            disabled={busy || r.awaiting_funding}
                            title={r.awaiting_funding ? "The rewards wallet does not cover this claim yet. Refill it first." : undefined}
                            onClick={() => pay(r)}
                          >
                            {busy ? "Working…" : "Pay with wallet"}
                          </button>
                        )}
                        {!entry && <button style={btnGhost} disabled={busy} onClick={() => cancel(r)}>Cancel</button>}
                      </div>
                    )}
                  </td>
                </tr>
                {(entry || failure) && (
                  <tr>
                    <td style={{ ...td, borderTop: "none", paddingTop: 0 }} colSpan={6}>
                      {entry && (
                        <div style={{ fontSize: 12 }}>
                          Transfer tx: <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{entry.txHash}</span>
                          {R.CHAIN_EXPLORER_URL && (
                            <> · <a href={`${R.CHAIN_EXPLORER_URL.replace(/\/$/, "")}/tx/${entry.txHash}`} target="_blank" rel="noreferrer">View</a></>
                          )}
                        </div>
                      )}
                      {failure && <div style={{ color: COLORS.error, fontSize: 12, marginTop: 4 }}>{failure}</div>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </Table>
      )}
      {!canPay && <ReadOnlyNote>Read only. Paying or cancelling a claim needs the rewards.payouts permission.</ReadOnlyNote>}
    </div>
  );
}

// ── Replay credit (recover a swap that did not credit) ───────────────────────
export function ReplayCreditCard({ flash }) {
  const [pollId, setPollId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const id = pollId.trim();
    if (!id) { flash("error", "Enter a poll id"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await replayRewardsCredit(id);
      setResult(r);
      if (r?.ok) flash("success", `Replayed poll ${id}. All handlers ok.`);
      else flash("info", `Replayed poll ${id}. Some handlers errored (see below).`);
    } catch (err) {
      flash("error", `Replay failed: ${apiError(err, "unknown error")}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <h5 style={{ marginTop: 0 }}>Replay credit for a poll</h5>
      <p style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 12, marginTop: 0 }}>
        Re-run reward crediting for a confirmed SwapKit, Rango or Chainflip swap that did not credit.
        Safe to re-run: already-credited polls are no-ops.
      </p>
      <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          style={{ ...input, width: 360, fontFamily: "monospace" }}
          placeholder="poll id (e.g. 6a2d8c86dabe00a64e109eb8)"
          value={pollId}
          onChange={(e) => setPollId(e.target.value)}
        />
        <button style={busyBtn(busy)} disabled={busy} type="submit">
          {busy ? "Replaying…" : "Replay"}
        </button>
      </form>
      {result && (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 6 }}>
            Poll <strong>{result.poll_id}</strong> ({result.chain}):{" "}
            {result.ok ? "all handlers ok" : "some handlers errored"}
          </div>
          {Array.isArray(result.results) && result.results.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Handler</th><th style={th}>Result</th></tr></thead>
              <tbody>
                {result.results.map((r) => (
                  <tr key={r.handler}>
                    <td style={td}>{r.handler}</td>
                    <td style={{ ...td, color: r.ok ? COLORS.success : COLORS.error }}>{r.ok ? "ok" : (r.error || "error")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Muted>No handlers ran.</Muted>}
        </div>
      )}
    </div>
  );
}
